import { useEffect, useRef, useState } from 'react';
import type { RefreshDirectorsResult } from '@/core/models/filing.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import { Button } from '@/kit/Button';
import { openDialog } from '@/kit/dialog';
import { ConfirmationDialog } from './ConfirmationDialog';

/**
 * Replaces the directors of every limited company / LLP client that has a registration number with the
 * current directors from Companies House (stored as Director contacts in the report settings).
 */
export function UpdateDirectors() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshing = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const report = (result: RefreshDirectorsResult) => {
    if (result.clientsChecked === 0) {
      notifyError('No limited company or LLP clients with a registration number were found.');
      return;
    }
    if (result.clientsUpdated > 0) {
      notifySuccess(
        `Directors refreshed for ${result.clientsUpdated} of ${result.clientsChecked} clients (${result.directorsImported} directors imported).`,
      );
    }
    if (result.failures.length > 0) {
      const details = result.failures
        .slice(0, 5)
        .map((f) => `${f.clientName}: ${f.message}`)
        .join(' ');
      const more = result.failures.length > 5 ? ` (+${result.failures.length - 5} more)` : '';
      notifyError(`${result.failures.length} client(s) could not be refreshed. ${details}${more}`);
    }
  };

  const finish = () => {
    refreshing.current = false;
    if (mounted.current) setIsRefreshing(false);
  };

  const refresh = () => {
    if (refreshing.current) {
      return;
    }
    refreshing.current = true;
    setIsRefreshing(true);
    filingService.refreshAllDirectors().then(
      (result) => {
        report(result);
        finish();
      },
      (error) => {
        notifyError(error);
        finish();
      },
    );
  };

  const openConfirmation = () => {
    const dialogRef = openDialog(ConfirmationDialog);

    dialogRef.afterClosed().then((confirmed) => {
      if (confirmed === true) {
        refresh();
      }
    });
  };

  return (
    <>
      <div className="form wp-100">
        <div className="content-body">
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              <p className="subtitle">Updating Companies House Directors</p>
            </div>
            {/* APT238: the refresh sits at the top, beside the heading, rather than in a bar at the foot of the page. */}
            <div className="content-header-right mb-1">
              <Button className="action-button primary-button" disabled={isRefreshing} onClick={openConfirmation}>
                Refresh All Company Directors
              </Button>
            </div>
          </div>

          <div className="content-header pt-1 mb-0 hr-top">
            <div className="content-header-left mb-1">
              <div className="text-gray font-size-14 fw-normal">
                <span className="fw-bold">Note: </span>
                This is an override feature. This is intended to be used by new clients when they are setting up in
                Capium. This will ensure that all the company officers are pulled in before the user proceeds with using
                the accounts production module.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
